if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelectorAll('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails.length) return; // Check for length

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));

        // Initialize thumbnails
        this.initThumbnails();

        // Set up MutationObserver to watch for changes in the subtree
        this.observer = new MutationObserver(this.onDOMChange.bind(this));
        this.observer.observe(this, { childList: true, subtree: true }); // Watching for child nodes only

        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      initThumbnails() {
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelectorAll('[id^="GalleryThumbnails"]'),
        };

        this.elements.thumbnails.forEach((slider) => {
          slider.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
            const button = mediaToSwitch.querySelector('ul button');
            button.addEventListener('click', (event) => {
              // Remove isActive class from all buttons
              slider.querySelectorAll('button').forEach((btn) => {
                btn.classList.remove('isActive');
              });

              // Add isActive class to the clicked button
              event.currentTarget.classList.add('isActive');
              this.replacePreviewImage(slider, mediaToSwitch);

              const thumbnailImage = event.currentTarget.querySelector('img');
              if (thumbnailImage) {
                const thumbnailImageId = thumbnailImage.getAttribute('id');

                const imageId_1 = thumbnailImageId.split(/-(.+)/)[1];

                this.elements.viewer.querySelectorAll('[data-media-id]').forEach((gallerySlideItem) => {
                  const galleryImage = gallerySlideItem.querySelector('.product__media img');
                  if (galleryImage) {
                    const galleryImageId = galleryImage.getAttribute('id');

                    const imageId_2 = galleryImageId.split(/-(.+)/)[1];

                    if (imageId_1 === imageId_2) {
                      gallerySlideItem.classList.add('is-active');
                    } else {
                      gallerySlideItem.classList.remove('is-active');
                    }
                  }
                });
              }
            });
          });

          // Set the first button to isActive on initial load
          const firstButton = slider.querySelector('ul button');
          if (firstButton) {
            firstButton.classList.add('isActive');
          }
        });
      }

      onDOMChange(mutations) {
        // Check if any added or removed nodes are detected
        const hasAddedOrRemovedNodes = mutations.some((mutation) => {
          if (mutation.type === 'childList') {
            // Ignore mutations if the target has an ID that starts with 'GalleryStatus-'
            if (mutation.target.id.startsWith('GalleryStatus-')) {
              return false; // Ignore this mutation
            }
            return mutation.addedNodes.length > 0 || mutation.removedNodes.length > 0;
          }
          return false;
        });

        if (hasAddedOrRemovedNodes) {
          // Reinitialize thumbnails
          this.initThumbnails();
          this.elements.thumbnails.forEach((slider) => {
            const buttons = slider.querySelectorAll('ul button');
            buttons.forEach((btn) => {
              btn.classList.remove('isActive');
            });
          });
          // Set the first button in each slider to isActive
          this.elements.thumbnails.forEach((slider) => {
            const firstButton = slider.querySelector('ul button');
            if (firstButton) {
              firstButton.classList.add('isActive');
            }
          });
        }
      }

      replacePreviewImage(slider, mediaToSwitch) {
        // Remove isActive from all buttons in the slider
        slider.querySelectorAll('[data-target]').forEach((mts) => {
          mts.querySelector('button').classList.remove('isActive');
        });
        // Add isActive class to the currently clicked button
        mediaToSwitch.querySelector('button').classList.add('isActive');

        const thumbnailImage = mediaToSwitch.querySelector('img'); // Get the thumbnail image
        const previewImage = document.querySelector('.preview_img img'); // Select the preview image
        if (thumbnailImage && previewImage) {
          previewImage.src = thumbnailImage.src; // Replace the src
          previewImage.srcset = thumbnailImage.srcset; // Update srcset if necessary
        }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails[0].querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails[0].querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail &&
              activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails[0].querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        const firstThumbnailGroup = this.elements.thumbnails[0];
        firstThumbnailGroup.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        if (!this.isSlideVisible(thumbnail, 10)) {
          firstThumbnailGroup.slider.scrollTo({ left: thumbnail.offsetLeft });
        }
      }

      isSlideVisible(thumbnail, offset = 0) {
        const thumbnailRect = thumbnail.getBoundingClientRect();
        const viewPortWidth = window.innerWidth;

        return thumbnailRect.left >= 0 - offset && thumbnailRect.right <= viewPortWidth + offset;
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
